import { Avatar, Box, Text } from '@factorialco/deprecated-design-system'
import { F0TagList } from '@factorialco/f0-react'
import {
  ProjectionKind,
  SignalsTag,
  TeamsTeam,
  useEmployeePulseEmployeeDatatable,
} from 'generated/resources/datatable.graphql'
import { compact } from 'lodash'
import moment from 'moment'

import DataTable from 'modules/core/components/DataTable'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  EmployeePulseIcon,
  getEmployeeMoodToolTip,
} from 'modules/engagement/components/EmployeePulseIcon'
import WithToolTip from 'modules/performance_analytics/components/WithTooltip'

import managerFilter from './filters/managerFilter'
import scoreFilter from './filters/scoreFilter'
import teamFilter from './filters/teamFilter'
import topicFilter from './filters/topicFilter'

type Props = {
  children?: React.ReactNode
  actions?: React.ReactNode
  dateRange: {
    to: Date
    from: Date
  }
  showTags?: boolean
  withTags?: boolean
  teams: Pick<TeamsTeam, 'id' | 'name'>[]
  tags: Pick<SignalsTag, 'id' | 'name'>[]
}

const EmployeePulseDatatable: React.FC<Props> = ({
  children,
  actions,
  dateRange,
  showTags = false,
  teams,
  tags: topics,
}) => {
  const source = useEmployeePulseEmployeeDatatable(
    [
      {
        identifier: 'submittedDate',
        title: i18n.t('engagement.pulse.dashboard.datatable.header.submitted_date'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ submittedDate }}',
        sortBy: 'submittedDate',
        render: (employeePulse) => {
          const date = employeePulse.submittedDate
          if (!date) return <Box width="s120" />

          // Keep in the backend timezone, to avoid timezone issues
          const formattedDate = i18nDateToString(moment.utc(date), 'ui')

          return (
            <Box width="s120">
              <Text.Normal size="200" weight="medium">
                {formattedDate}
              </Text.Normal>
            </Box>
          )
        },
      },
      {
        identifier: 'employee',
        title: i18n.t('engagement.pulse.dashboard.datatable.header.employee'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ access_employee_fullName }}',
        sortBy: 'fullName',
        render: (employeePulse) => {
          const employee = employeePulse.access?.employee
          const access = employeePulse.access

          return (
            employee &&
            access && (
              <Box flexDirection="row" alignItems="center" gap="s16" width="s200">
                <Avatar size="s40" url={buildUrl(access.avatar)} label={employee.fullName} />
                <Text.Normal size="200" weight="medium">
                  {employee.fullName}
                </Text.Normal>
              </Box>
            )
          )
        },
      },
      {
        identifier: 'value',
        title: i18n.t('engagement.pulse.dashboard.datatable.header.pulse_value'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ value }}',
        sortBy: 'value',
        render: (employeePulse) => {
          const value = employeePulse.value

          return (
            <Box width="s96" alignItems="center">
              <WithToolTip
                label={value ? getEmployeeMoodToolTip(value) : ''}
                trackingGroup="pulse-survey"
              >
                <EmployeePulseIcon mood={value} />
              </WithToolTip>
            </Box>
          )
        },
      },
      {
        identifier: 'tags',
        title: i18n.t('engagement.pulse.dashboard.datatable.header.topics'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ tags }}',
        hidden: !showTags,
        render: (employeePulse) => {
          const tags = employeePulse.tags

          return (
            <F0TagList
              max={2}
              tags={tags.map((tag) => ({
                text: tag.signalName,
                color: 'viridian',
              }))}
              type="dot"
            />
          )
        },
      },
      {
        identifier: 'comment',
        title: i18n.t('engagement.pulse.dashboard.datatable.header.comment'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ comment }}',
        render: (employeePulse) => {
          const comment = employeePulse.comment

          return (
            <Box width="s300">
              <Text.Normal size="200" weight="normal">
                {comment || '-'}
              </Text.Normal>
            </Box>
          )
        },
      },
    ],
    {
      variables: {
        from: moment(dateRange.from).format('YYYY-MM-DD'),
        to: moment(dateRange.to).format('YYYY-MM-DD'),
      },
    }
  )

  return (
    <DataTable.Provider
      filters={compact([
        managerFilter(source),
        scoreFilter(source),
        teamFilter(source, teams),
        showTags ? topicFilter(source, topics) : null,
      ])}
      sort={{ path: 'submittedDate', direction: 'desc' }}
      source={source}
    >
      <Box gap="s12">
        <DataTable.HeadBar
          exportFileName="employee-pulse-export"
          disableEditColumns
          inlineDefaultActions
          actions={actions}
        />
        {children}
        <DataTable.Table autoWidth />
      </Box>
    </DataTable.Provider>
  )
}

export default EmployeePulseDatatable
